import type { ActiveRoomSnapshot, GameResult } from "@topzle/types";
import { create } from "zustand";

interface GuestIdentity {
  id: string;
  token: string;
  displayName: string;
  avatar: string;
}
export interface RoomSession {
  roomId: string;
  participantId: string;
  displayName: string;
  avatar: string;
  websocketUrl: string;
  reconnectToken: string;
  /** Shared, unguessable Supabase Broadcast topic key for P2P signaling fallback. */
  realtimeSignalToken?: string;
  snapshot: ActiveRoomSnapshot;
  lastVisitedAt: number;
}
export interface GameActivity {
  messageId: string;
  roomId: string;
  playerId: string;
  gameId: string;
  versusAi: boolean;
  startedAt: number;
  result?: GameResult;
  completedAt?: number;
}
interface AppState {
  guest: GuestIdentity | null;
  room: RoomSession | null;
  recentRooms: RoomSession[];
  gameActivity: GameActivity[];
  layout: string;
  microphone: boolean;
  camera: boolean;
  setGuest: (guest: GuestIdentity) => void;
  clearGuest: () => void;
  setRoom: (room: RoomSession) => void;
  updateRoomSnapshot: (roomId: string, snapshot: ActiveRoomSnapshot) => void;
  forgetRoom: (roomId: string) => void;
  recordGameStarted: (activity: GameActivity) => void;
  recordGameResult: (roomId: string, result: GameResult, completedAt: number) => void;
  setLayout: (layout: string) => void;
  toggleMicrophone: () => void;
  toggleCamera: () => void;
}
const readStored = <T>(key: string, fallback: T): T => {
  if (typeof localStorage === "undefined") return fallback;
  try {
    const value = localStorage.getItem(key);
    return value ? (JSON.parse(value) as T) : fallback;
  } catch {
    return fallback;
  }
};
function guestTokenExpiresAt(token: string): number | null {
  try {
    const signedToken = token.startsWith("guest_") ? token.slice(6) : token;
    const encodedPayload = signedToken.split(".")[0];
    if (!encodedPayload) return null;
    const base64 = encodedPayload.replaceAll("-", "+").replaceAll("_", "/");
    const payload = JSON.parse(atob(base64.padEnd(Math.ceil(base64.length / 4) * 4, "="))) as {
      exp?: unknown;
    };
    return typeof payload.exp === "number" ? payload.exp : null;
  } catch {
    return null;
  }
}

const persistedGuest = readStored<GuestIdentity | null>("topzle.guest", null);
const guestExpiry = persistedGuest ? guestTokenExpiresAt(persistedGuest.token) : null;
const storedGuest =
  persistedGuest && guestExpiry && guestExpiry > Date.now() ? persistedGuest : null;
if (persistedGuest && !storedGuest && typeof localStorage !== "undefined")
  localStorage.removeItem("topzle.guest");
const storedRooms = readStored<RoomSession[]>("topzle.rooms", [])
  .filter((room) => room?.roomId && room?.reconnectToken)
  .map((room) => ({
    ...room,
    lastVisitedAt: room.lastVisitedAt ?? room.snapshot?.updatedAt ?? Date.now(),
  }));
const storedActivity = readStored<GameActivity[]>("topzle.game-activity", []);
const persistRooms = (rooms: RoomSession[]) =>
  localStorage.setItem("topzle.rooms", JSON.stringify(rooms.slice(0, 12)));
export const useAppStore = create<AppState>((set) => ({
  guest: storedGuest,
  room: storedRooms[0] ?? null,
  recentRooms: storedRooms,
  gameActivity: storedActivity,
  layout: "bubbles",
  microphone: false,
  camera: false,
  setGuest: (guest) => {
    localStorage.setItem("topzle.guest", JSON.stringify(guest));
    set({ guest });
  },
  clearGuest: () => {
    localStorage.removeItem("topzle.guest");
    set({ guest: null });
  },
  setRoom: (room) =>
    set((state) => {
      const saved = { ...room, lastVisitedAt: room.lastVisitedAt || Date.now() };
      const recentRooms = [
        saved,
        ...state.recentRooms.filter((item) => item.roomId !== room.roomId),
      ];
      persistRooms(recentRooms);
      return { room: saved, recentRooms: recentRooms.slice(0, 12) };
    }),
  updateRoomSnapshot: (roomId, snapshot) =>
    set((state) => {
      const activeAlreadyCurrent =
        state.room?.roomId !== roomId || state.room.snapshot === snapshot;
      const recentAlreadyCurrent = state.recentRooms.every(
        (room) => room.roomId !== roomId || room.snapshot === snapshot,
      );
      if (activeAlreadyCurrent && recentAlreadyCurrent) return state;
      const visitedAt = Date.now();
      const update = (room: RoomSession) =>
        room.roomId === roomId ? { ...room, snapshot, lastVisitedAt: visitedAt } : room;
      const recentRooms = state.recentRooms.map(update);
      persistRooms(recentRooms);
      return {
        recentRooms,
        room: state.room?.roomId === roomId ? update(state.room) : state.room,
      };
    }),
  forgetRoom: (roomId) =>
    set((state) => {
      const recentRooms = state.recentRooms.filter((room) => room.roomId !== roomId);
      persistRooms(recentRooms);
      return { recentRooms, room: state.room?.roomId === roomId ? null : state.room };
    }),
  recordGameStarted: (activity) =>
    set((state) => {
      if (state.gameActivity.some((item) => item.messageId === activity.messageId)) return state;
      const gameActivity = [activity, ...state.gameActivity].slice(0, 500);
      localStorage.setItem("topzle.game-activity", JSON.stringify(gameActivity));
      return { gameActivity };
    }),
  recordGameResult: (roomId, result, completedAt) =>
    set((state) => {
      const index = state.gameActivity.findIndex((item) => item.roomId === roomId && !item.result);
      if (index < 0) return state;
      const gameActivity = state.gameActivity.map((item, itemIndex) =>
        itemIndex === index ? { ...item, result, completedAt } : item,
      );
      localStorage.setItem("topzle.game-activity", JSON.stringify(gameActivity));
      return { gameActivity };
    }),
  setLayout: (layout) => set({ layout }),
  toggleMicrophone: () => set((state) => ({ microphone: !state.microphone })),
  toggleCamera: () => set((state) => ({ camera: !state.camera })),
}));
